import { CloseOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Heading, Spinner } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { useAIEmailTemplateCRUDActions } from '@mm/state/actions/AIEmailTemplatesCRUDActions';
import { useAutomationsCRUDActions } from '@mm/state/actions/automationActions';
import { hideSlideoutMenu } from '@mm/state/actions/viewActions';
import { SlideOutMenuType } from '@mm/state/interfaces/view';
import WidgetModel, {
  EmailConfig,
  EmailConfigEntities,
  WidgetBase,
  WidgetTypes,
} from '@mm/state/model/WidgetModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectActiveEmailAccounts } from '@mm/state/selectors/emailAccountsSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { widgetsByDialogueSelector } from '@mm/state/selectors/widgetSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { notification, Switch, Tooltip as AntTooltip } from 'antd';
import classNames from 'classnames';
import { debounce, isEqual } from 'lodash';
import { Node } from 'prosemirror-model';
import 'prosemirror-view/style/prosemirror.css';
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { useCommonWidgetHandlers } from '../../../../../common/helpers/widget-helpers/use-common-widget-handlers';
import { useEmailWidgetHandlers } from '../../../../../common/helpers/widget-helpers/use-email-widget-handlers';
import { useDetectOutsideClicks } from '../../../../../common/hooks/use-detect-outside-clicks';
import { useFreeTrialUpgradeModal } from '../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { Menu } from '../../../../features/beta/modules/Automations/AutomationEdit/DialogueEditor/NodeTypes/elements/NodeMenu/NodeMenu';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import { EmailForm } from './EmailForm/EmailForm';
import { EMAIL_TEMPLATES } from './EmailTemplates';
import { EmailTemplatesTable } from './EmailTemplatesTable/EmailTemplatesTable';
import { EmailTemplateUrlsAdder } from './EmailTemplateUrlsAdder/EmailTemplateUrlsAdder';
import { useEmailTemplatesLandings } from './EmailTemplateUrlsAdder/use-email-templates-landings';
import { NoEmailAccountsWarning } from './NoEmailAccountsWarning/NoEmailAccountsWarning';
import css from './SlideoutEmail.module.css';
import { SmartSendingInfo } from './SmartSendingInfo/SmartSendingInfo';
import aiToolExplainer from './svg/ai-tool-explainer.png';
import { useEditor } from './use-editor-old';

// For a newer version of react-dropzone, use:
// const ACCEPTED_FILE_TYPES = {
//   'application/pdf': ['.pdf'],
//   'application/msword': ['.doc'],
//   'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
//     '.docx',
//   ],
//   'application/vnd.ms-excel': ['.xls'],
//   'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
//     '.xlsx',
//   ],
//   'application/vnd.ms-powerpoint': ['.ppt'],
//   'application/vnd.openxmlformats-officedocument.presentationml.presentation': [
//     '.pptx',
//   ],
//   'image/jpeg': ['.jpg', '.jpeg'],
//   'image/png': ['.png'],
//   'image/bmp': ['.bmp'],
//   'image/gif': ['.gif'],
//   'image/tiff': ['.tif', '.tiff'],
//   'image/vnd.adobe.photoshop': ['.psd'],
//   'application/vnd.adobe.illustrator': ['.ai'],
//   'text/plain': ['.txt'],
//   'text/rtf': ['.rtf'],
//   'application/x-mobipocket-ebook': ['.mobi'],
//   'application/epub+zip': ['.epub'],
// };

export type ISlideoutEmail = {
  className?: string;
  chatbotId?: number;
  dialogueId?: number;
  title?: ReactNode;
  showSmartSendingInfo?: boolean;
  navigateWidget?: WidgetModel;
  widgetId?: number;
};

export type ISlideoutEmailWithWidget = ISlideoutEmail & {
  widget: WidgetModel;
};

export const useSlideoutOnClose = (
  onClose: () => Promise<unknown> = async () => {},
  menuType?: SlideOutMenuType,
) => {
  const [wrapperRef, setWrapperRef] = useState<HTMLElement | null>(null);

  const reduxDispatch: Dispatch<StoreState> = useDispatch();
  const { openNewConfirmModal } = useModalActions();

  const menu = useSelector((state: StoreState) => state.view.slideoutMenu);
  const lastSlideout = menu[menu.length - 1];

  const close = () => reduxDispatch(hideSlideoutMenu());

  useDetectOutsideClicks(async e => {
    if (menuType && lastSlideout.menuType !== menuType) {
      return;
    }

    if (Array.from((e.target as Element)?.classList)?.includes('js-overlay')) {
      try {
        await onClose();
        close();
      } catch (err) {}
    }
  }, wrapperRef);

  const setRef = (ref: HTMLElement) => {
    setWrapperRef(ref);
  };

  const onBeforeClose = (initialValue, updatedValue) => {
    const hasNoChanges = isEqual(initialValue, updatedValue);
    if (hasNoChanges) {
      return Promise.resolve(true);
    }

    return new Promise((resolve, reject) => {
      openNewConfirmModal({
        title: 'You have unsaved changes',
        subtitle: 'Are you sure you want to leave without saving?',
        onReject: async () => reject(),
        onResolve: async close => {
          close();
          resolve(true);
        },
        resolveButton: {
          children: "Yes, I'm sure",
        },
      });
    });
  };

  return {
    setRef,
    wrapperRef,
    close,
    onBeforeClose,
  };
};

const SlideoutEmailTemplateList: React.FC<ISlideoutEmailWithWidget> = props => {
  const { className, widget } = props;
  const { setRef, close } = useSlideoutOnClose();

  const [name, setName] = useState('');

  const commonWidgetHandlers = useCommonWidgetHandlers();

  const updateWidget = async (emailTemplateId: string) => {
    const template = EMAIL_TEMPLATES[emailTemplateId];
    if (!template) {
      return;
    }

    await commonWidgetHandlers.update({
      ...widget,
      config: {
        ...widget.config,
        subject: template.subject,
        body: template.body,
        previewText: template.previewText,
      },
    });
  };

  return (
    <SlideoutMenu
      className={classNames(className, css.container, css.listContainer)}
      setRef={setRef}
    >
      <div className={css.headerContainer}>
        <span className={css.close} onClick={close}>
          <CloseOutlined />
        </span>

        <h1 className={css.listHeading}>Searching for Inspiration?</h1>
      </div>

      {/* EXPECTED TO BE USED  */}
      {/*<section>*/}
      {/*  <CollapsibleContentContainer*/}
      {/*    orderNumber="1"*/}
      {/*    containerName="Give your email a name so you can refer to it later"*/}
      {/*    stepperStylingPlatform="facebook-inverted"*/}
      {/*    useCircleNumberStyle*/}
      {/*    expandedOnMount*/}
      {/*    hideExpandIcon*/}
      {/*    hideTitleBorder*/}
      {/*  >*/}
      {/*    <Input*/}
      {/*      css={{ container: css.input }}*/}
      {/*      value={name}*/}
      {/*      onChange={e => setName(e.target.value)}*/}
      {/*    />*/}
      {/*  </CollapsibleContentContainer>*/}
      {/*</section>*/}

      <section>
        {/*<CollapsibleContentContainer*/}
        {/*  orderNumber="1"*/}
        {/*  containerName="Pick one of our pre-written email templates to increase email*/}
        {/*  engagement"*/}
        {/*  stepperStylingPlatform="facebook-inverted"*/}
        {/*  useCircleNumberStyle*/}
        {/*  expandedOnMount*/}
        {/*  hideExpandIcon*/}
        {/*  hideTitleBorder*/}
        {/*>*/}
        <div className={css.list}>
          {Object.entries(EMAIL_TEMPLATES).map(([id, template]) => (
            <div className={css.listItem} key={id}>
              <div className={css.listIcon}>
                <template.listIcon />
              </div>
              <div className={css.listText}>
                <span className={css.listTitle}>{template.title}</span>
                <span className={css.listDescription}>
                  {template.listDescription}
                </span>
              </div>
              <Button
                size="sm"
                onClick={() => updateWidget(id)}
                css={{ container: css.button }}
              >
                Choose
              </Button>
            </div>
          ))}
        </div>
        {/*</CollapsibleContentContainer>*/}
      </section>
    </SlideoutMenu>
  );
};

const compareNodes = debounce(
  (l: Node, r: Node) => l.toString() !== r.toString(),
  300,
  { leading: true, trailing: false },
);

const SlideoutEmailEditor: React.FC<ISlideoutEmailWithWidget> = props => {
  const {
    title,
    className,
    widget,
    navigateWidget,
    chatbotId,
    showSmartSendingInfo,
  } = props;

  const accounts = useSelector(selectActiveEmailAccounts);
  const automationsCRUDActions = useAutomationsCRUDActions();
  const commonWidgetHandlers = useCommonWidgetHandlers();
  const stateAndHandlers = useEmailWidgetHandlers({ widget }, false);
  const {
    previewTextValue,
    subjectValue,
    bodyValue,
    ccEmails,
    bccEmails,
    attachments,
    setAttachments,
    configEntities,
    setConfigEntities,

    cloneEntityToMaster,
    bodyJsonValue,
    bodyDdValue,
    ddEditorValue,
  } = stateAndHandlers;

  const { selectedLandings, onChangeSelectedLandings } =
    useEmailTemplatesLandings({
      widget,
      onDeleteRemote: url =>
        setConfigEntities(configEntities.filter(e => e.url !== url)),
    });

  const {
    config: {
      subject,
      body,
      previewText,
      cc,
      bcc,
      aiCustomizer,
      entities,
      ddEditor,
      bodyJson,
      bodyDd,
    },
  } = widget as unknown as WidgetBase<EmailConfig>;

  const currentMasterEntity = configEntities?.find(entity => entity.master);
  const { reset: aiReset } = useAIEmailTemplateCRUDActions();

  const [aiEnabled, setAiEnabled] = useState(aiCustomizer);

  const cache = useRef({
    subject,
    body,
    previewText,
    ddEditor,
    bodyJson,
    bodyDd,
  });

  const facebookPageId = useSelector(activeFacebookPageIdSelector);

  const modalActions = useModalActions();
  const { openFreeTrialUpgradeModal } = useFreeTrialUpgradeModal();
  const onUpgradeClick = async () => {
    try {
      await onBeforeClose();
      closeSlideout();
      openFreeTrialUpgradeModal({
        landmineName: landmineNames.EMAIL_EDITOR_REMOVE_BRANDING,
      });
    } catch {}
  };

  const openSendTestEmailModal = () => {
    if (chatbotId) {
      modalActions.openSendTestEmailModal({
        chatbotId,
        onSubmit: async emails => {
          if (facebookPageId) {
            const emailAttachmentIds = attachments
              .map(a => a.id)
              .filter(a => !!a) as number[];
            await automationsCRUDActions.sendTestEmail({
              emails,
              facebookPageId,
              widgetId: widget.id,
              body: ddEditor ? bodyDd || '' : bodyValue,
              subject: subjectValue,
              preview: previewTextValue,
              emailAttachments: emailAttachmentIds.map(id => ({ id })),
            });
          }
        },
      });
    }
  };

  const onSendTestEmailClick = () => {
    if (hasNoChanges) {
      openSendTestEmailModal();
      return;
    }

    modalActions.openNewConfirmModal({
      title: 'You have unsaved changes',
      subtitle: 'Do you want to save your changes before sending test email?',
      onResolve: async () => {
        await onSave();
        openSendTestEmailModal();
      },
      rejectButton: {
        children: 'Continue without saving',
        onClick: async () => openSendTestEmailModal(),
      },
      resolveButton: {
        children: 'Save and go to test',
      },
    });
  };

  const onDeleteClick = () => {
    const deleteCallback = async () => {
      if (navigateWidget) {
        await commonWidgetHandlers.delete(navigateWidget);
        closeSlideout();
      }
    };

    const headerText = 'Are you sure you want to delete this email widget?';

    modalActions.openDeleteConfirmModal({
      deleteCallback,
      headerText,
      newStyle: true,
    });
  };

  const editor = useEditor(bodyValue);

  const [busy, setBusy] = useState(false);
  const [waitingForAttachmentUpload, setWaitingForAttachmentUpload] =
    useState(false);

  const hasAnyEntityEmptyBody = !configEntities.every(
    entity => entity.body || entity.emailAttachments?.length,
  );

  const onSave = async () => {
    setBusy(true);
    try {
      if (attachments.some(a => !!a.uploadPromise)) {
        setWaitingForAttachmentUpload(true);
        await Promise.all(attachments.map(a => a.uploadPromise));
        setWaitingForAttachmentUpload(false);
      }

      if (hasAnyEntityEmptyBody) {
        notification.error({
          message: 'Email body cannot be blank.',
          description: 'Add email body to your templates.',
          placement: 'topLeft',
        });
        return;
      }

      const payload = {
        aiCustomizer: aiEnabled,
        subject: subjectValue,
        previewText: previewTextValue,
        body: bodyValue,
        bodyDd: bodyDdValue,
        bodyJson: bodyJsonValue,
        ddEditor: ddEditorValue,
        emailAttachments: attachments,
        cc: ccEmails.join(','),
        bcc: bccEmails.join(','),
        entities: configEntities,
      };

      const savedWidget: WidgetBase<EmailConfig> =
        await commonWidgetHandlers.update({
          ...widget,
          config: {
            ...widget.config,
            ...payload,
          },
        });
      if (savedWidget?.config?.entities) {
        setConfigEntities(savedWidget.config.entities);
      }

      notification.success({
        message: 'Your changes have been saved.',
        placement: 'topLeft',
      });

      setAttachments(attachments =>
        attachments
          .filter(a => !a.deleted && !a.uploadError)
          .map(a => ({ ...a, added: undefined })),
      );
      cache.current = payload;
      editor.setDoc(editor.state.doc);
    } catch (ee) {
      notification.error({
        message: 'Something went wrong',
        placement: 'topLeft',
      });
    } finally {
      setBusy(false);
    }
  };

  const hasSubjectPreviewOrBodyChanges = useMemo(
    () =>
      cache.current.subject !== subjectValue ||
      cache.current.previewText !== previewTextValue ||
      cache.current.body !== bodyValue,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subjectValue, previewTextValue, bodyValue, cache.current],
  );

  const hasDdEditorBodyChanges = useMemo(
    () =>
      cache.current.bodyDd !== bodyDdValue ||
      cache.current.bodyJson !== bodyJsonValue ||
      cache.current.ddEditor !== ddEditorValue,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bodyDdValue, bodyJsonValue, ddEditorValue, cache.current],
  );

  const remapAttachments = (array: EmailConfigEntities[], without = true) => {
    if (without) {
      return array.map(({ emailAttachments, ...entity }) => entity);
    }
    return array.map(({ emailAttachments }) => emailAttachments);
  };

  const hasAttachmentChanges = attachments.some(a => a.deleted || a.added);
  const hasCChanges = ccEmails.join(',') !== (cc || '');
  const hasBCChanges = bccEmails.join(',') !== (bcc || '');
  const hasAIToolChanges = aiEnabled !== aiCustomizer;
  const hasEntriesChanges = !isEqual(
    remapAttachments(entities || []),
    remapAttachments(configEntities),
  );
  const hasEntriesAttachmentChanges = remapAttachments(
    configEntities,
    false,
  ).some(a => a?.deleted || a?.added);

  const hasNoChanges = [
    aiEnabled
      ? hasEntriesChanges
      : hasSubjectPreviewOrBodyChanges || hasDdEditorBodyChanges,
    hasAttachmentChanges,
    hasBCChanges,
    hasCChanges,
    hasAIToolChanges,
    hasEntriesAttachmentChanges,
  ].every(v => !v);

  const isSaveDisabled = busy;

  const onBeforeClose = () => {
    if (hasNoChanges) {
      aiReset();
      return Promise.resolve(true);
    }

    return new Promise((resolve, reject) => {
      modalActions.openNewConfirmModal({
        title: 'You have unsaved changes',
        subtitle: 'Are you sure you want to leave without saving?',
        onReject: async () => {
          aiReset();
          closeSlideout();
          reject();
        },
        onResolve: async close => {
          aiReset();
          await onSave();
          close();
          if (!hasAnyEntityEmptyBody) {
            resolve(true);
          }
        },
        rejectButton: { children: 'Leave without saving' },
        resolveButton: {
          children: 'Save changes',
        },
      });
    });
  };

  const close = async () => {
    try {
      await onBeforeClose();
      closeSlideout();
    } catch (err) {}
  };

  const { setRef, close: closeSlideout } = useSlideoutOnClose(onBeforeClose);

  const renderTitle = title || <Heading size={4}>Edit an Email</Heading>;

  const onEditSignatureClick = async () => {
    modalActions.openEmailAccountEditorModal({
      emailAccountUnderEdit: accounts?.[0],
    });
  };

  const onToggleSwitch = (active: boolean) => {
    if (!active) {
      modalActions.openNewConfirmModal({
        title: 'Are you sure you want to turn A.I. Emails Generator off?',
        subtitle: 'Don’t worry. We will save your customized emails.',
        withImage: false,
        onResolve: async close => {
          setAiEnabled(active);
          close();
        },
        rejectButton: { children: 'Cancel' },
        resolveButton: {
          children: 'Turn off',
        },
        css: {
          container: css.confirmModal,
          body: css.confirmModalBody,
          title: css.confirmModalTitle,
          subtitle: css.confirmModalSubtitle,
          cancelButton: css.confirmModalCancel,
        },
      });
    } else {
      setAiEnabled(active);
      if (!currentMasterEntity) {
        onChangeSelectedLandings('master');

        const masterEntity: EmailConfigEntities = {
          url: 'master',
          master: true,
          subject: subjectValue,
          previewText: previewTextValue,
          emailAttachments: attachments,
          body: bodyValue,
          cc: ccEmails.join(','),
          bcc: bccEmails.join(','),
        };
        setConfigEntities([masterEntity]);
      }
    }
    if (currentMasterEntity) {
      cloneEntityToMaster(currentMasterEntity);
    }
  };

  const menu = [
    {
      disabled: false,
      children: 'Send me test email',
      onClick: onSendTestEmailClick,
    },
    {
      disabled: !navigateWidget,
      children: 'Delete',
      onClick: onDeleteClick,
    },
  ];

  return (
    <SlideoutMenu
      className={classNames(className, css.container)}
      setRef={setRef}
    >
      <div className={css.header}>
        {renderTitle}

        <div className={css.headerButtons}>
          <Button
            css={{ container: css.primaryButton }}
            onClick={onSave}
            disabled={isSaveDisabled}
          >
            Save
          </Button>
          <Menu
            className={css.menu}
            menu={menu}
            direction="bottom"
            useThreeDotButton
            css={{ menu: css.nodeMenu }}
          />
          <span className={css.close} onClick={close}>
            <CloseOutlined />
          </span>
        </div>
      </div>
      {showSmartSendingInfo && <SmartSendingInfo />}

      <NoEmailAccountsWarning />

      <SlideoutMenu.Content className={css.content}>
        <div className={css.aiContainer}>
          <div className={css.aiTag}>A.I. Tool</div>
          <AntTooltip
            overlayClassName={css.aiTooltip}
            overlayInnerStyle={{ backgroundColor: '#fbfcfe' }}
            title={<img src={aiToolExplainer} />}
            placement="bottomRight"
            getPopupContainer={triggerNode =>
              triggerNode.parentElement || document.body
            }
          >
            <div className={css.aiTutorial}>
              <QuestionCircleOutlined />
              How does our A.I. tool work?
            </div>
          </AntTooltip>

          <div className={css.aiHeader}>
            <Switch
              size="small"
              checked={aiEnabled}
              onChange={onToggleSwitch}
              className={css.aiSwitch}
            />
            <div className={css.aiTitle}>
              <div style={{ fontWeight: 500 }}>A.I. Email Customizer</div>
              <div>Customize the email based on the URL the user lands on</div>
            </div>
          </div>
        </div>
        {aiEnabled && (
          <>
            <EmailTemplatesTable
              selectedLandings={selectedLandings}
              onChangeSelectedLandings={onChangeSelectedLandings}
              widget={widget}
              onUpgradeClick={onUpgradeClick}
              stateAndHandlers={stateAndHandlers}
              onSignatureClick={onEditSignatureClick}
            />
            <EmailTemplateUrlsAdder
              selectedLandings={selectedLandings}
              onChangeSelectedLandings={onChangeSelectedLandings}
              stateAndHandlers={stateAndHandlers}
            />
          </>
        )}
        {!aiEnabled && (
          <div className={classNames(css.boxWrapper)}>
            <EmailForm
              widget={widget}
              entityUrl={currentMasterEntity ? 'master' : undefined}
              onUpgradeClick={onUpgradeClick}
              stateAndHandlers={stateAndHandlers}
              onSignatureClick={onEditSignatureClick}
            />
          </div>
        )}
      </SlideoutMenu.Content>
      {waitingForAttachmentUpload && (
        <div className={css.waitingForAttachmentUploadWrapper}>
          <div className={css.waitingForAttachmentUpload}>
            <Spinner standalone size={16} />
            <div>Waiting for attachments to finish uploading...</div>
          </div>
        </div>
      )}
    </SlideoutMenu>
  );
};

export const SlideoutEmail: React.FC<ISlideoutEmail> = props => {
  const { dialogueId, navigateWidget, widgetId } = props;

  const { setRef, close } = useSlideoutOnClose();

  const widgets = useSelector((state: StoreState) =>
    widgetsByDialogueSelector(dialogueId, state.widget.entities),
  );

  const widget = widgets.find(w =>
    widgetId ? w.id === widgetId : w.type === WidgetTypes.EMAIL,
  );

  if (!widget) {
    return (
      <SlideoutMenu setRef={setRef}>
        <SlideoutMenu.Header className="" closeIcon="close">
          Ooops..
        </SlideoutMenu.Header>
        <SlideoutMenu.Content>Something went wrong</SlideoutMenu.Content>
      </SlideoutMenu>
    );
  }

  const config = widget.config as unknown as EmailConfig;

  // temp disabled per request
  // if (
  //   [config.subject, config.body, config.previewText].every(
  //     attr => attr === null,
  //   )
  // ) {
  //   return <SlideoutEmailTemplateList widget={widget} {...props} />;
  // }

  return (
    <SlideoutEmailEditor
      navigateWidget={navigateWidget}
      widget={widget}
      {...props}
    />
  );
};
